// src/components/AnomalyView.js
import React, { useEffect, useState } from "react";
import {
  getAnomalyScore,
  getAnomalyMode,
  getAnomalyWeights,
} from "../api/anomaly";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

function getScoreColor(score) {
  if (score < 0.3) return "#4caf50"; // green
  if (score < 0.6) return "#ff9800"; // amber
  return "#f44336"; // red
}

const AnomalyView = () => {
  const [score, setScore] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [mode, setMode] = useState(null);
  const [weights, setWeights] = useState(null);
  const [regimeMultipliers, setRegimeMultipliers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const scoreData = await getAnomalyScore();
        const weightsData = await getAnomalyWeights();

        setScore(scoreData.score);
        setBreakdown(scoreData.breakdown);
        setMode(scoreData.mode);
        setWeights(weightsData.weights);
        setRegimeMultipliers(weightsData.regime_multipliers);
      } catch (err) {
        setError("Failed to load anomaly data. Please check your connection.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [retryCount]);

  if (loading) {
    return (
      <ResponsiveContainer>
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={150} />
        </Box>
      </ResponsiveContainer>
    );
  }

  const categoryLabels = {
    M: "Market-driven",
    S: "Screener-driven",
    P: "Portfolio-driven",
    D: "Provider-driven",
  };

  const modeChipColor = mode === "PREMIUM_BURST_MODE" ? "error" : "default";
  const modeChipLabel =
    mode === "PREMIUM_BURST_MODE" ? "⚡ Premium Burst Mode" : "⏱️ Normal Metered Mode";

  return (
    <ResponsiveContainer>
      <Box sx={{ mt: 2 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom>
          Anomaly Engine
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Real-time anomaly score and category breakdown from the market anomaly
          controller.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                component="button"
                onClick={() => setRetryCount((c) => c + 1)}
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "bold",
                }}
              >
                Retry
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Overall Score Badge */}
        {score !== null && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              mb: 3,
              borderLeft: `6px solid ${getScoreColor(score)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Overall Anomaly Score
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: getScoreColor(score),
                my: 1,
              }}
            >
              {(score * 100).toFixed(1)}%
            </Typography>
            <Chip label={modeChipLabel} color={modeChipColor} variant="outlined" />
          </Paper>
        )}

        {/* Category Breakdown */}
        {breakdown && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Category Breakdown
              </Typography>
              <Stack spacing={2}>
                {Object.entries(breakdown).map(([category, value]) => (
                  <Box key={category}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {category}: {categoryLabels[category] || category}
                      </Typography>
                      <Typography variant="body2">{(value * 100).toFixed(1)}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={value * 100} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Weights Table */}
        {weights && regimeMultipliers && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weight Configuration (Read-Only)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Base Weight</TableCell>
                      <TableCell align="right">Regime Multiplier</TableCell>
                      <TableCell align="right">Effective Weight</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(weights).map(([category, weight]) => {
                      const multiplier = regimeMultipliers[category] || 1.0;
                      const effective = weight * multiplier;
                      return (
                        <TableRow key={category}>
                          <TableCell>
                            {category}: {categoryLabels[category] || category}
                          </TableCell>
                          <TableCell align="right">{weight.toFixed(2)}</TableCell>
                          <TableCell align="right">{multiplier.toFixed(2)}</TableCell>
                          <TableCell align="right" fontWeight={600}>
                            {effective.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </ResponsiveContainer>
  );
};

export default AnomalyView;
